// Considered: ChannelSubscriptionsTabOrganism + SubscribedSpheresListOrganism — not rejected;
// this organism reconciles/replaces the disconnected tab/list split into the design's single
// grouped card. Old organisms remain as gate=false fallback until post-soak cleanup.
// See spec Reuse Audit (specs/sphere-selector-and-subscriptions.md).
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import {
  getSubscribedSpheres,
  unsubscribeSphereFromChannel,
  updateSphereSubscriptionScope,
  SubscribedSphereItem,
} from '../../../api/marketplaceApi';
import type { ScopeMode } from 'types/channel';
import {
  SubscribeButtonAtom,
  SubscribeButtonState,
} from '../../atoms/marketplace/SubscribeButtonAtom';
import HelpTipAtom from '../../atoms/HelpTip/HelpTipAtom';
import SphereSubscriptionRowMolecule from '../../molecules/marketplace/SphereSubscriptionRowMolecule';
import SpherePickerDialogOrganism from './SpherePickerDialogOrganism';
import './SubscriptionsPanelOrganism.scss';

// Ops-tunable defaults; Phase 4 can wire to Statsig dynamic config sphere_selector_limits.
const ORG_SPHERES_LIST_CAP = 8;
const PAGE_LIMIT = 20;

export interface SubscriptionsPanelOrganismProps {
  channelId: string;
  organizationId: string;
  isOwner: boolean;
  /** SubscribeButtonAtom state for the personal "You" subscription row. */
  subscribeState: SubscribeButtonState | null;
  isTogglingSubscription: boolean;
  onPersonalSubscribeClick: () => void;
  /** Sphere ID of the navbar-selected sphere — enables the Nearby tab in the picker. */
  nearbyAnchorSphereId?: string;
}

const SubscriptionsPanelOrganism: React.FC<SubscriptionsPanelOrganismProps> = ({
  channelId,
  organizationId,
  isOwner,
  subscribeState,
  isTogglingSubscription,
  onPersonalSubscribeClick,
  nearbyAnchorSphereId,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const orgId = organizationId || impersonationDetails?.organizationId || '';

  const [subs, setSubs] = useState<SubscribedSphereItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // IDs already subscribed — passed to picker to prevent duplicates.
  const subscribedSphereIds = subs.map((s) => s.sphere_id);

  const fetchSubscriptions = useCallback(async () => {
    if (!apiClients?.schemaClient) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getSubscribedSpheres(apiClients.schemaClient, channelId, {
        limit: PAGE_LIMIT,
      });
      setSubs(response.data);
      setTotal(response.total);
    } catch {
      setError(t('sphereChannels.subscriptionsPanel.loadError'));
    } finally {
      setLoading(false);
    }
  }, [apiClients, channelId, orgId, t]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const handleRemove = useCallback(
    async (subscriptionId: string, sphereId: string) => {
      if (!apiClients?.schemaClient) return;
      setRemovingId(subscriptionId);
      try {
        await unsubscribeSphereFromChannel(apiClients.schemaClient, subscriptionId, sphereId);
        setSubs((prev) => prev.filter((s) => s.id !== subscriptionId));
        setTotal((prev) => prev - 1);
      } catch {
        setError(t('sphereChannels.subscriptionsPanel.removeError'));
      } finally {
        setRemovingId(null);
      }
    },
    [apiClients, t]
  );

  const handleScopeChange = useCallback(
    async (subscriptionId: string, sphereId: string, scopeMode: ScopeMode) => {
      if (!apiClients?.schemaClient) return;
      // Optimistic update
      setSubs((prev) =>
        prev.map((s) => (s.id === subscriptionId ? { ...s, scope_mode: scopeMode } : s))
      );
      try {
        await updateSphereSubscriptionScope(
          apiClients.schemaClient,
          subscriptionId,
          scopeMode,
          sphereId
        );
      } catch {
        // Roll back on failure
        setError(t('sphereChannels.subscriptionsPanel.updateScopeError'));
        fetchSubscriptions();
      }
    },
    [apiClients, t, fetchSubscriptions]
  );

  const handleSubscribed = useCallback(() => {
    setPickerOpen(false);
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const helpTipText = isOwner
    ? t('sphereChannels.subscriptionsPanel.helpTip.owner')
    : t('sphereChannels.subscriptionsPanel.helpTip.viewer');

  const orgSpheresHelpText = t('sphereChannels.subscriptionsPanel.orgSpheresSection.helpTip');

  // Cap visible rows at ORG_SPHERES_LIST_CAP; count badge shows true total.
  const visibleSubs = subs.slice(0, ORG_SPHERES_LIST_CAP);

  return (
    <div className="subscriptions-panel">
      {/* Header */}
      <div className="subscriptions-panel__header">
        <span className="subscriptions-panel__title">
          {t('sphereChannels.subscriptionsPanel.title')}
        </span>
        <HelpTipAtom text={helpTipText} className="subscriptions-panel__header-tip" />
      </div>

      {/* "You" row — non-owners only */}
      {!isOwner && subscribeState && (
        <div className="subscriptions-panel__you-section">
          <span className="subscriptions-panel__section-label">
            {t('sphereChannels.subscriptionsPanel.youSection.label')}
          </span>
          <SubscribeButtonAtom
            state={subscribeState}
            loading={isTogglingSubscription}
            onClick={onPersonalSubscribeClick}
            className="subscriptions-panel__you-btn"
          />
        </div>
      )}

      {/* "Organization spheres" group */}
      <div className="subscriptions-panel__org-section">
        <div className="subscriptions-panel__org-header">
          <span className="subscriptions-panel__section-label">
            {t('sphereChannels.subscriptionsPanel.orgSpheresSection.label')}
          </span>
          <HelpTipAtom text={orgSpheresHelpText} />
          <span className="subscriptions-panel__spacer" />
          {total > 0 && (
            <span className="subscriptions-panel__count-badge" aria-label={t('sphereChannels.subscriptionsPanel.countBadgeAriaLabel', { count: total })}>
              {total}
            </span>
          )}
        </div>

        {error && (
          <p className="subscriptions-panel__error" role="alert">{error}</p>
        )}

        {(() => {
          if (loading) {
            return (
              <div className="subscriptions-panel__loading">
                <i className="pi pi-spin pi-spinner" aria-hidden="true" />
              </div>
            );
          }
          if (visibleSubs.length > 0) {
            return (
              <div className="subscriptions-panel__list">
                {visibleSubs.map((sub) => (
                  <SphereSubscriptionRowMolecule
                    key={sub.id}
                    subscriptionId={sub.id}
                    sphereId={sub.sphere_id}
                    scopeMode={sub.scope_mode}
                    canManage={sub.canManage}
                    onRemove={handleRemove}
                    onScopeChange={handleScopeChange}
                    isRemoving={removingId === sub.id}
                  />
                ))}
              </div>
            );
          }
          return (
            <p className="subscriptions-panel__empty">
              {t('sphereChannels.subscriptionsPanel.orgSpheresSection.empty')}
            </p>
          );
        })()}

        <button
          type="button"
          className="subscriptions-panel__subscribe-btn"
          onClick={() => setPickerOpen(true)}
        >
          <i className="pi pi-plus" aria-hidden="true" />
          {t('sphereChannels.subscriptionsPanel.orgSpheresSection.subscribeButton')}
        </button>
      </div>

      {pickerOpen && (
        <SpherePickerDialogOrganism
          open={pickerOpen}
          channelId={channelId}
          subscribedSphereIds={subscribedSphereIds}
          nearbyAnchorSphereId={nearbyAnchorSphereId}
          onClose={() => setPickerOpen(false)}
          onSubscribed={handleSubscribed}
        />
      )}
    </div>
  );
};

export default SubscriptionsPanelOrganism;
